import { useState } from 'react';
import { ScoreClient, ScoreDTO, ApiResponse_string, UpdateScoreStatusDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useScoreApi = () => {
    const apiClient = new ScoreClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: ScoreDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createScore(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Score');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: ScoreDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateScore(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Score');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getScoreById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Score');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchScores(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Scores');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteScore(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Score');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportScores(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting Scores', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromDeleted = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromDeletedScore(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Course from deleted state');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (data: UpdateScoreStatusDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateScoreQuickStatus(data);
        } catch (err: any) {
            setError(err.message || 'Error updating score status');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getFeedback = async (id: string): Promise<string | undefined> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getScoreFeedback(id);
            return response.data ?? undefined;
        } catch (err: any) {
            setError(err.message || 'Error fetching Score feedback');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getDistinctYears = async (): Promise<number[]> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getScoreYears();
            return (response.data as unknown as number[]) ?? [];
        } catch (err: any) {
            setError(err.message || 'Error fetching score years');
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, restoreFromDeleted, remove, updateStatus, getDistinctYears, getFeedback, isLoading, error };
};